import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2, MoreHorizontal, FileText, Mail, ScrollText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResourcesList() {
  const navigate = useNavigate();
  const [newsletters, setNewsletters] = useState([]);
  const [regulatory, setRegulatory] = useState([]);
  const [representations, setRepresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchNewsletter, setSearchNewsletter] = useState('');
  const [searchRegulatory, setSearchRegulatory] = useState('');
  const [searchRepresentation, setSearchRepresentation] = useState('');
  const [deleteNewsletterId, setDeleteNewsletterId] = useState(null);
  const [deleteRegulatoryId, setDeleteRegulatoryId] = useState(null);
  const [deleteRepresentationId, setDeleteRepresentationId] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/newsletters?include_inactive=true`, { withCredentials: true }),
      axios.get(`${API}/admin/regulatory-updates?include_inactive=true`, { withCredentials: true }),
      axios.get(`${API}/admin/representations?include_inactive=true`, { withCredentials: true }),
    ])
      .then(([nRes, rRes, repRes]) => {
        setNewsletters(Array.isArray(nRes.data) ? nRes.data : []);
        setRegulatory(Array.isArray(rRes.data) ? rRes.data : []);
        setRepresentations(Array.isArray(repRes.data) ? repRes.data : []);
      })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), []);

  const handleDeleteNewsletter = async () => {
    if (!deleteNewsletterId) return;
    try {
      await axios.delete(`${API}/admin/newsletters/${deleteNewsletterId}`, { withCredentials: true });
      toast.success('Deleted');
      setNewsletters(newsletters.filter((i) => i.newsletter_id !== deleteNewsletterId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setDeleteNewsletterId(null);
    }
  };

  const handleDeleteRegulatory = async () => {
    if (!deleteRegulatoryId) return;
    try {
      await axios.delete(`${API}/admin/regulatory-updates/${deleteRegulatoryId}`, { withCredentials: true });
      toast.success('Deleted');
      setRegulatory(regulatory.filter((i) => i.update_id !== deleteRegulatoryId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setDeleteRegulatoryId(null);
    }
  };

  const handleDeleteRepresentation = async () => {
    if (!deleteRepresentationId) return;
    try {
      await axios.delete(`${API}/admin/representations/${deleteRepresentationId}`, { withCredentials: true });
      toast.success('Deleted');
      setRepresentations(representations.filter((i) => i.representation_id !== deleteRepresentationId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setDeleteRepresentationId(null);
    }
  };

  const filteredNewsletters = newsletters.filter(
    (i) =>
      (i.title || '').toLowerCase().includes(searchNewsletter.toLowerCase()) ||
      (i.content || '').toLowerCase().includes(searchNewsletter.toLowerCase())
  );
  const filteredRegulatory = regulatory.filter(
    (i) =>
      (i.title || '').toLowerCase().includes(searchRegulatory.toLowerCase()) ||
      (i.summary || '').toLowerCase().includes(searchRegulatory.toLowerCase())
  );
  const filteredRepresentations = representations.filter(
    (i) =>
      (i.title || '').toLowerCase().includes(searchRepresentation.toLowerCase()) ||
      (i.summary || '').toLowerCase().includes(searchRepresentation.toLowerCase())
  );

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Resources</h1>
        <p className="text-gray-500">
          Manage newsletters, regulatory updates, and representations for the Resources page
        </p>
      </div>

      {/* Newsletters */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#C8473A]" />
            Newsletters
          </h2>
          <Button
            onClick={() => navigate('/admin/resources/new?type=newsletter')}
            className="bg-[#C8473A] hover:bg-[#B74F40]"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Newsletter
          </Button>
        </div>
        <div className="mb-4">
          <div className="relative max-w-md">
            <Input
              placeholder="Search newsletters..."
              value={searchNewsletter}
              onChange={(e) => setSearchNewsletter(e.target.value)}
              className="pl-4"
            />
          </div>
        </div>
        <div className="bg-white rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredNewsletters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                    No newsletters yet.
                  </TableCell>
                </TableRow>
              ) : (
                filteredNewsletters.map((item) => (
                  <TableRow key={item.newsletter_id}>
                    <TableCell>
                      <p className="font-medium max-w-md truncate">{item.title}</p>
                      {item.is_featured && (
                        <Badge className="mt-1 bg-[#C8473A] text-white text-xs">Featured</Badge>
                      )}
                    </TableCell>
                    <TableCell>{new Date(item.published_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Badge variant={item.is_active ? 'default' : 'secondary'}>
                        {item.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => navigate(`/admin/resources/newsletters/${item.newsletter_id}`)}
                          >
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeleteNewsletterId(item.newsletter_id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Regulatory Updates */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#C8473A]" />
            Regulatory Updates
          </h2>
          <Button
            onClick={() => navigate('/admin/resources/new?type=regulatory')}
            className="bg-[#C8473A] hover:bg-[#B74F40]"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Regulatory Update
          </Button>
        </div>
        <div className="mb-4">
          <div className="relative max-w-md">
            <Input
              placeholder="Search regulatory updates..."
              value={searchRegulatory}
              onChange={(e) => setSearchRegulatory(e.target.value)}
              className="pl-4"
            />
          </div>
        </div>
        <div className="bg-white rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Summary</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredRegulatory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No regulatory updates yet.
                  </TableCell>
                </TableRow>
              ) : (
                filteredRegulatory.map((item) => (
                  <TableRow key={item.update_id}>
                    <TableCell>
                      <p className="font-medium max-w-xs truncate">{item.title}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-500 max-w-md truncate">{item.summary || '—'}</p>
                    </TableCell>
                    <TableCell>{new Date(item.published_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Badge variant={item.is_active ? 'default' : 'secondary'}>
                        {item.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(`/admin/resources/regulatory-updates/${item.update_id}`)
                            }
                          >
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeleteRegulatoryId(item.update_id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Representations */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-[#C8473A]" />
            Representations
          </h2>
          <Button
            onClick={() => navigate('/admin/resources/new?type=representation')}
            className="bg-[#C8473A] hover:bg-[#B74F40]"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Representation
          </Button>
        </div>
        <div className="mb-4">
          <div className="relative max-w-md">
            <Input
              placeholder="Search representations..."
              value={searchRepresentation}
              onChange={(e) => setSearchRepresentation(e.target.value)}
              className="pl-4"
            />
          </div>
        </div>
        <div className="bg-white rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Summary</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredRepresentations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No representations yet.
                  </TableCell>
                </TableRow>
              ) : (
                filteredRepresentations.map((item) => (
                  <TableRow key={item.representation_id}>
                    <TableCell>
                      <p className="font-medium max-w-xs truncate">{item.title}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-500 max-w-md truncate">{item.summary || '—'}</p>
                    </TableCell>
                    <TableCell>{new Date(item.published_date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Badge variant={item.is_active ? 'default' : 'secondary'}>
                        {item.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(`/admin/resources/representations/${item.representation_id}`)
                            }
                          >
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeleteRepresentationId(item.representation_id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      <AlertDialog
        open={!!deleteNewsletterId}
        onOpenChange={(open) => !open && setDeleteNewsletterId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete newsletter?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button onClick={handleDeleteNewsletter} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={!!deleteRegulatoryId}
        onOpenChange={(open) => !open && setDeleteRegulatoryId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete regulatory update?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button onClick={handleDeleteRegulatory} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={!!deleteRepresentationId}
        onOpenChange={(open) => !open && setDeleteRepresentationId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete representation?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button onClick={handleDeleteRepresentation} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
